/**
 * <rain-effect> – natural canvas rain for Feature banner
 * Attributes: data-density, data-speed, data-angle, data-opacity, data-size,
 *             data-length, data-splash, data-mobile
 * data-mobile="true"  → rain also runs on screens up to 768px (lighter version)
 * data-mobile="false" → desktop only
 */
if (!customElements.get('rain-effect')) {
  class RainEffect extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.density = Number(this.dataset.density || 120);
      this.speed = Number(this.dataset.speed || 10) / 10;
      this.angle = (Number(this.dataset.angle || 8) * Math.PI) / 180;
      this.opacity = Number(this.dataset.opacity || 50) / 100;
      this.splash = this.dataset.splash === 'true';
      this.size = Number(this.dataset.size || 10) / 10;
      this.lengthScale = Number(this.dataset.length || 10) / 10;
      this.mobileAllowed = this.dataset.mobile === 'true';

      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');

      this.drops = [];
      this.splashes = [];
      this.running = false;
      this.visible = false;
      this.last = 0;
      this.tick = this.tick.bind(this);

      this.mq = window.matchMedia('(max-width: 768px)');
      this.onMq = () => {
        this.drops = [];
        this.update();
      };
      this.mq.addEventListener('change', this.onMq);

      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this);

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.update();
      });
      this.io.observe(this);

      this.onVis = () => this.update();
      document.addEventListener('visibilitychange', this.onVis);
    }

    disconnectedCallback() {
      this.stop();
      this.ro && this.ro.disconnect();
      this.io && this.io.disconnect();
      this.mq && this.mq.removeEventListener('change', this.onMq);
      document.removeEventListener('visibilitychange', this.onVis);
    }

    get isMobile() {
      return this.mq.matches;
    }

    /* true when we are on mobile and mobile rain is switched off */
    get blocked() {
      return this.isMobile && !this.mobileAllowed;
    }

    get allowed() {
      return this.visible && !document.hidden && !this.blocked;
    }

    update() {
      if (this.allowed) {
        if (!this.drops.length) this.resize();
        this.start();
      } else {
        this.stop();
        if (this.blocked && this.w) this.ctx.clearRect(0, 0, this.w, this.h);
      }
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }

    stop() {
      this.running = false;
      cancelAnimationFrame(this.raf);
    }

    resize() {
      if (this.blocked) return;
      const rect = this.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      // Phones: cap pixel ratio lower to save battery
      const dpr = Math.min(window.devicePixelRatio || 1, this.isMobile ? 1.5 : 2);
      const widthChanged = Math.abs((this.w || 0) - rect.width) > 2;

      this.w = rect.width;
      this.h = rect.height;
      this.canvas.width = Math.round(this.w * dpr);
      this.canvas.height = Math.round(this.h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Scale drop count to area; mobile gets a lighter amount
      const area = (this.w * this.h) / (1440 * 700);
      const mobileFactor = this.isMobile ? 1.4 : 1; // small screens need a little boost to look full
      const min = this.isMobile ? 25 : 30;
      const count = Math.max(min, Math.round(this.density * Math.min(area * mobileFactor, 1.6)));

      // Mobile address bar show/hide changes only the height — keep existing drops
      // so the rain doesn't "jump"; only rebuild when width changes or count differs.
      if (!this.drops.length || widthChanged) {
        this.drops = Array.from({ length: count }, () => this.makeDrop(true));
      } else if (this.drops.length < count) {
        while (this.drops.length < count) this.drops.push(this.makeDrop(true));
      } else if (this.drops.length > count) {
        this.drops.length = count;
      }
    }

    makeDrop(initial) {
      const z = 0.25 + Math.random() * 0.75; // depth: far (small, slow, faint) → near
      const len = (8 + z * 22) * this.lengthScale;
      const vy = (9 + z * 15) * this.speed;
      const spread = Math.abs(Math.tan(this.angle)) * this.h;
      return {
        z,
        len,
        vy,
        x: Math.random() * (this.w + spread * 2) - spread,
        y: initial ? Math.random() * this.h : -len - Math.random() * this.h * 0.3,
        ground: this.h * (0.75 + Math.random() * 0.25),
      };
    }

    tick(now) {
      if (!this.running) return;
      const dt = Math.min((now - this.last) / 16.67, 3); // normalise to 60fps
      this.last = now;

      // Gentle gusts for a natural feel
      const wind = this.angle + Math.sin(now * 0.0004) * 0.035 + Math.sin(now * 0.0013) * 0.015;
      const tan = Math.tan(wind);

      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.w, this.h);
      ctx.lineCap = 'round';

      for (let i = 0; i < this.drops.length; i++) {
        const d = this.drops[i];
        const vx = d.vy * tan;
        d.y += d.vy * dt;
        d.x += vx * dt;

        const tailX = d.x - tan * d.len;
        const tailY = d.y - d.len;
        const a = this.opacity * (0.25 + d.z * 0.55);

        const grad = ctx.createLinearGradient(tailX, tailY, d.x, d.y);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(1, `rgba(220,230,240,${a})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = (0.6 + d.z * 0.9) * this.size;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(d.x, d.y);
        ctx.stroke();

        const landed = this.splash && d.z > 0.6 ? d.y >= d.ground : d.y - d.len > this.h;
        if (landed) {
          if (this.splash && d.z > 0.6) this.addSplash(d.x, d.y, d.z);
          this.drops[i] = this.makeDrop(false);
        }
      }

      if (this.splash) this.drawSplashes(dt);

      this.raf = requestAnimationFrame(this.tick);
    }

    addSplash(x, y, z) {
      const n = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i++) {
        this.splashes.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 2.2 * z,
          vy: -(0.8 + Math.random() * 1.6) * z,
          life: 1,
          r: (0.5 + Math.random() * 0.8 * z) * this.size,
        });
      }
      const max = this.isMobile ? 200 : 400;
      if (this.splashes.length > max) this.splashes.splice(0, this.splashes.length - max);
    }

    drawSplashes(dt) {
      const ctx = this.ctx;
      for (let i = this.splashes.length - 1; i >= 0; i--) {
        const p = this.splashes[i];
        p.vy += 0.12 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.life -= 0.04 * dt;
        if (p.life <= 0) {
          this.splashes.splice(i, 1);
          continue;
        }
        ctx.fillStyle = `rgba(220,230,240,${this.opacity * 0.7 * p.life})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  customElements.define('rain-effect', RainEffect);
}
